import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { robustFetch } from '../utils/apiUtils';
import './PodcastDisplay.css';

type PodcastSection = {
    text: string;
    desc: string;
    image: string;
};

type PodcastEpisode = {
    _id?: string;
    slug?: string;
    title?: string;
    date?: string;
    createdAt?: string;
    thumbnail?: string;
    audioFile?: string;
    podcastNumber?: number | string;
};

const PODCAST_SECTIONS: PodcastSection[] = [
  { text: "Episode available in all popular platforms", desc: "Listen on Spotify, Apple Podcasts, YouTube and more", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomePodcast+Image+1.png" },
  { text: "Carefully designed for your convenience", desc: "A listening experience built around you", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomePodcast+Image+2.png" },
  { text: "Listen with transcript and switch chapters", desc: "Follow along with text and jump between chapters easily", image: "https://home-asset.s3.eu-north-1.amazonaws.com/HomePodcast+Image+3.png" },
];

export default function PodcastDisplay() {
    const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
    const [loading, setLoading] = useState(true);
    const [podActiveSection, setPodActiveSection] = useState(0);
    const [podImgLoaded, setPodImgLoaded] = useState(false);
    const podIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const clearPodInterval = useCallback(() => {
        if (podIntervalRef.current !== null) {
            clearInterval(podIntervalRef.current);
            podIntervalRef.current = null;
        }
    }, []);

    const startPodCycle = useCallback(() => {
        clearPodInterval();
        podIntervalRef.current = setInterval(() => {
            setPodActiveSection(prev => (prev + 1) % PODCAST_SECTIONS.length);
        }, 6000);
    }, [clearPodInterval]);

    useEffect(() => {
        startPodCycle();
        return () => clearPodInterval();
    }, [startPodCycle, clearPodInterval]);

    useEffect(() => {
        // Load cached episodes immediately for fast display
        let hasCachedData = false;
        try {
            const cached = sessionStorage.getItem('home_podcast_episodes_v1');
            if (cached) {
                const cachedEpisodes = JSON.parse(cached);
                if (Array.isArray(cachedEpisodes) && cachedEpisodes.length > 0) {
                    setEpisodes(cachedEpisodes);
                    setLoading(false);
                    hasCachedData = true;
                }
            }
        } catch(e) {}

        const fetchPodcasts = async () => {
            try {
                const response = await robustFetch(API_ENDPOINTS.PODCAST.ALL);
                if (!response.ok) throw new Error('Failed to fetch podcasts');

                // Safe JSON parse guard against Render.com cold-start returning "undefined"
                const rawText = await response.text();
                const trimmed = rawText ? rawText.trim() : '';
                if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                    if (!hasCachedData) setLoading(false);
                    return;
                }
                let data: unknown;
                try { data = JSON.parse(trimmed); } catch(e) {
                    if (!hasCachedData) setLoading(false);
                    return;
                }

                if (Array.isArray(data)) {
                    // Sort by date (newest first)
                    const sortedEpisodes = data.sort((a: PodcastEpisode, b: PodcastEpisode) => {
                        const dateA = new Date(a.date || a.createdAt || 0);
                        const dateB = new Date(b.date || b.createdAt || 0);
                        return dateB.getTime() - dateA.getTime();
                    });

                    const top3 = sortedEpisodes.slice(0, 3);
                    // Cache for instant display on next visit within same session
                    try { sessionStorage.setItem('home_podcast_episodes_v1', JSON.stringify(top3)); } catch(e) {}
                    setEpisodes(top3);
                } else {
                    console.error('PodcastDisplay: Data is not an array', data);
                }
            } catch (error) {
                console.error('Error fetching podcasts for display:', error);
            } finally {
                if (!hasCachedData) setLoading(false);
            }
        };

        fetchPodcasts();
    }, []);

    // Helper to correct thumbnail URL (reused logic)
    const getThumbnailUrl = (podcast: PodcastEpisode): string => {
        if (!podcast) return "/images/home_images/Logo.png";
        
        // Prioritize constructing from audio file pattern as the thumbnail field might be outdated/broken
        try {
            if (podcast.audioFile) {
                const audioMatch = podcast.audioFile.match(/\/(?:Podcast-|episode-)(\d+)\//);
                const podcastNumber = audioMatch ? audioMatch[1] : null;
                if (podcastNumber) {
                    return `https://podcast-audio-episodes.s3.eu-north-1.amazonaws.com/episode-${podcastNumber}/Podcast+${podcastNumber}+Thumbnail.jpg`;
                }
            }
        } catch (e) {
            console.error('Error generating thumbnail URL', e);
        }
        
        // Fallback to thumbnail field if construction fails
        if (podcast.thumbnail) return podcast.thumbnail;
        
        return "/images/home_images/Logo.png"; // Fallback
    };

    return (
        <section className="podcast-display-section mx-auto my-[2%] w-[90%] max-w-[2000px] overflow-hidden rounded-[40px] bg-[#7132CA] px-[2rem] py-[3rem] text-white box-border max-[1024px]:px-[1rem] max-[1024px]:py-[2rem]">
            <div className="podcast-display-container mx-auto flex items-stretch gap-[4rem] max-[1024px]:flex-col max-[1024px]:gap-[2rem]">
                <div className="podcast-display-left flex flex-[1.5] flex-row-reverse items-center gap-[2rem] text-white max-[1024px]:mb-[1rem] max-[1024px]:w-full max-[1024px]:flex-col">
                    <div className="pod-feat-left-col flex flex-1 flex-col gap-[1rem] max-[1024px]:contents">
                        <h2 className="podcast-display-title m-0 mb-[0.25rem] text-[2rem] font-[700] leading-[1] tracking-[-2px] max-[480px]:order-1 max-[480px]:text-[clamp(2rem,8vw,2.5rem)] min-[481px]:max-[767px]:text-[clamp(1.8rem,6vw,2.5rem)] min-[768px]:max-[1024px]:order-1 min-[768px]:max-[1024px]:text-[clamp(2.2rem,5vw,3rem)]">
                            Listen to my<br />Podcast...
                        </h2>
                        <div className="pod-feat-left flex flex-col max-[480px]:order-3 max-[480px]:h-[120px] max-[480px]:items-center max-[480px]:justify-center max-[480px]:overflow-hidden min-[481px]:max-[767px]:h-[120px] min-[481px]:max-[767px]:items-center min-[481px]:max-[767px]:justify-center min-[481px]:max-[767px]:overflow-hidden min-[768px]:max-[1024px]:order-3 min-[768px]:max-[1024px]:h-[120px] min-[768px]:max-[1024px]:items-center min-[768px]:max-[1024px]:justify-center min-[768px]:max-[1024px]:overflow-hidden">
                            <div className="pod-feat-list flex flex-col gap-[0.25rem]">
                                {PODCAST_SECTIONS.map((section, index) => (
                                    <div
                                        key={index}
                                        className={`pod-feat-item relative cursor-pointer overflow-hidden rounded-r-[8px] py-[0.8rem] pr-[1rem] pl-[calc(1rem+4px)]${podActiveSection === index ? ' active' : ''}`}
                                        onClick={() => { setPodActiveSection(index); startPodCycle(); }}
                                    >
                                        <span className="pod-feat-item-text block text-[1.25rem] font-[700] text-white opacity-40 transition-opacity duration-300 max-[480px]:text-[1rem] min-[481px]:max-[767px]:text-[1.1rem]">{section.text}</span>
                                        <span className="pod-feat-item-desc mt-[0.2rem] block text-[0.88rem] font-[400] text-white opacity-50 transition-opacity duration-300">{section.desc}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                    <div className="pod-feat-images relative min-h-[280px] flex-[1.2] self-stretch overflow-hidden rounded-[20px] max-[480px]:order-2 max-[480px]:min-h-[200px] max-[480px]:w-full min-[481px]:max-[767px]:min-h-[220px] min-[481px]:max-[767px]:w-full min-[768px]:max-[1024px]:order-2 min-[768px]:max-[1024px]:min-h-[260px] min-[768px]:max-[1024px]:w-full">
                        {!podImgLoaded && <div className="pod-feat-skeleton skeleton absolute inset-0 z-[2] rounded-[20px]" />}
                        {PODCAST_SECTIONS.map((section, index) => (
                            <img
                                key={index}
                                src={section.image}
                                alt={section.text}
                                className={`pod-feat-img pointer-events-none absolute inset-0 h-full w-full origin-center rounded-[20px] object-contain opacity-0 transition-all duration-500 ${podActiveSection === index ? 'active scale-[1.07] opacity-100' : 'scale-100'}`}
                                onLoad={() => setPodImgLoaded(true)}
                            />
                        ))}
                    </div>
                </div>
                <div className="podcast-display-right relative flex flex-1 flex-col justify-center overflow-hidden max-[1024px]:w-full">
                    <h4 className="podcast-label relative z-[2] m-[0_1.5rem_1.5rem_1.5rem] text-[0.9rem] font-[700] uppercase tracking-[1px] text-[#66ff99]">MOST RECENT</h4>
                    <div className="vertical-label pointer-events-none absolute right-[20px] top-1/2 -translate-y-1/2 whitespace-nowrap font-['Bebas_Neue',sans-serif] text-[clamp(16px,4vh,64px)] font-[600] leading-[1.2] tracking-[1px] opacity-50 [writing-mode:vertical-rl] [text-orientation:mixed] max-[480px]:text-[clamp(16px,3.5vh,64px)] min-[481px]:max-[767px]:text-[clamp(16px,3vh,64px)] min-[768px]:max-[1024px]:text-[clamp(16px,2.5vh,64px)]">MOST RECENT</div>
                    {loading ? (
                         [1, 2, 3].map((item) => (
                            <React.Fragment key={item}>
                                <div className="podcast-display-item relative z-[1] flex items-center justify-center p-[1.5rem] text-inherit no-underline transition-colors duration-200 hover:rounded-[15px] hover:bg-white/5">
                                    <div className="skeleton skeleton-badge"></div>
                                    <div className="skeleton skeleton-thumbnail"></div>
                                    <div className="skeleton-info">
                                        <div className="skeleton skeleton-title"></div>
                                        <div className="skeleton skeleton-date" style={{ width: '40%', marginTop: '0.5rem' }}></div>
                                    </div>
                                </div>
                                {item < 3 && <div className="podcast-display-separator mx-auto h-[1px] w-[95%] bg-white/20"></div>}
                            </React.Fragment>
                         ))
                    ) : (
                        episodes.map((episode, index) => {
                            if (!episode) return null;
                            return (
                                <React.Fragment key={episode._id || index}>
                                    <NavLink 
                                        to={`/podcast/${episode.slug || ''}`} 
                                        className="podcast-display-item relative z-[1] flex items-center justify-center p-[1.5rem] text-inherit no-underline transition-colors duration-200 hover:rounded-[15px] hover:bg-white/5"
                                    >
                                        <img
                                            src={getThumbnailUrl(episode)}
                                            alt={episode.title || 'Episode'}
                                            className="episode-thumbnail mr-[1.5rem] h-[80px] w-[80px] rounded-[8px] bg-[#333] object-cover max-[767px]:h-[60px] max-[767px]:w-[60px] min-[768px]:max-[1024px]:h-[60px] min-[768px]:max-[1024px]:w-[60px]"
                                            loading="lazy"
                                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                e.currentTarget.src = "/images/home_images/Logo.png";
                                                e.currentTarget.onerror = null; // Prevent infinite loop
                                            }}
                                        />
                                        <div className="episode-info flex-1">
                                            <div className="episode-meta mb-[0.25rem] flex w-full items-center justify-between">
                                                <span className="episode-num-small text-[0.85rem] font-[600] uppercase tracking-[0.5px] text-white/60 max-[767px]:text-[0.75rem] min-[768px]:max-[1024px]:text-[0.8rem]">
                                                    #{(() => {
                                                        if (episode.podcastNumber) return episode.podcastNumber;
                                                        if (episode.audioFile) {
                                                            const epMatch = episode.audioFile.match(/\/episode-(\d+)\//);
                                                            if (epMatch && epMatch[1]) return epMatch[1];
                                                            const match = episode.audioFile.match(/\/(?:Podcast-|episode-)(\d+)\//);
                                                            if (match && match[1]) return match[1];
                                                        }
                                                        return index + 1;
                                                    })()}
                                                </span>
                                                <div className="episode-date text-[0.85rem] font-[600] uppercase tracking-[0.5px] text-white/60 max-[767px]:text-[0.75rem] min-[768px]:max-[1024px]:text-[0.8rem]">
                                                    {new Date(episode.date || episode.createdAt || 0).toLocaleDateString('en-GB', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </div>
                                            <h3 className="episode-title m-0 block text-[1.2rem] font-[600] leading-[1.3] text-white max-[767px]:text-[1rem] min-[768px]:max-[1024px]:text-[1.1rem]">{episode.title || 'Untitled Episode'}</h3>
                                        </div>
                                    </NavLink>
                                    {index < episodes.length - 1 && <div className="podcast-display-separator mx-auto h-[1px] w-[95%] bg-white/20"></div>}
                                </React.Fragment>
                            );
                        })
                    )}
                    <NavLink to="/podcast" className="home-podcast-btn mx-auto mt-[2rem] flex h-[40px] w-[120px] items-center justify-center rounded-[12px] border-0 bg-primary-cyan text-[1.1rem] font-[700] text-white no-underline transition-all duration-300 hover:-translate-y-[2px] hover:bg-[#2ea3ff] hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)]">View All</NavLink>
                </div>
            </div>
        </section>
    );
}
